import { useId, type ComponentProps } from "react";
import { Field, FieldLabel } from "@volca/ui/components/ui/field";
import { Input } from "@volca/ui/components/ui/input";
import { Stack } from "@volca/ui/components/ui/stack";
import type { ApPoLineItem } from "@/accounts-payable/types";
import { SectionLabel } from "./shared";

const LineField = ({
  label,
  ...props
}: { label: string } & ComponentProps<typeof Input>) => {
  const id = useId();
  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Input id={id} {...props} />
    </Field>
  );
};

export type LineItemDraft = Omit<ApPoLineItem, "quantity" | "unit_cost"> & {
  quantity: number | string;
  unit_cost: number | string;
};

/**
 * Line items as one list: description full width, quantity + unit cost side
 * by side, a divider between items — the view mode's list, not a card each.
 */
export const LineItemsEditor = ({
  items,
  onChange,
}: {
  items: LineItemDraft[];
  onChange: (index: number, key: keyof ApPoLineItem, value: string) => void;
}) => (
  <Stack className="w-full" align="start" gap={2}>
    <SectionLabel>Line items ({items.length})</SectionLabel>
    <Stack align="start" gap={0} className="w-full divide-y divide-line">
      {items.map((it, i) => (
        <Stack
          align="start"
          key={i}
          gap={2}
          className="w-full py-3 first:pt-0 last:pb-0"
        >
          <LineField
            label="Description"
            value={it.description}
            onChange={(e) => onChange(i, "description", e.target.value)}
          />
          <Stack className="w-full" align="start" direction="row" gap={4}>
            <LineField
              label="Quantity"
              type="text"
              inputMode="numeric"
              value={it.quantity}
              onChange={(e) => onChange(i, "quantity", e.target.value)}
            />
            <LineField
              label="Unit cost ($)"
              type="text"
              inputMode="decimal"
              value={it.unit_cost}
              onChange={(e) => onChange(i, "unit_cost", e.target.value)}
            />
          </Stack>
        </Stack>
      ))}
    </Stack>
  </Stack>
);
